import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type DropdownPortalProps = {
	anchorRef: React.RefObject<HTMLElement>;
	open: boolean;
	children: React.ReactNode;
	className?: string;
};

export function DropdownPortal({
	anchorRef,
	open,
	children,
	className = "tiptap-toolbar__dropdown",
}: DropdownPortalProps) {
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const dropdownRef = useRef<HTMLDivElement>(null);

	const updatePosition = useCallback(() => {
		if (!anchorRef.current) return;
		const rect = anchorRef.current.getBoundingClientRect();
		let left = rect.left;
		const top = rect.bottom + 4;

		if (dropdownRef.current) {
			const dropdownWidth = dropdownRef.current.offsetWidth;
			const viewportWidth = window.innerWidth;

			if (left + dropdownWidth > viewportWidth - 8) {
				left = viewportWidth - dropdownWidth - 8;
			}

			if (left < 8) {
				left = 8;
			}
		}

		setPosition({ top, left });
	}, [anchorRef]);

	useEffect(() => {
		if (!open) return;
		updatePosition();
		requestAnimationFrame(updatePosition);

		const toolbar = anchorRef.current?.closest(".tiptap-toolbar");
		const onScroll = () => updatePosition();
		const onResize = () => updatePosition();

		if (toolbar) {
			toolbar.addEventListener("scroll", onScroll);
		}
		window.addEventListener("resize", onResize);
		window.addEventListener("scroll", onResize, true);

		return () => {
			if (toolbar) {
				toolbar.removeEventListener("scroll", onScroll);
			}
			window.removeEventListener("resize", onResize);
			window.removeEventListener("scroll", onResize, true);
		};
	}, [open, updatePosition, anchorRef]);

	if (!open) return null;

	return createPortal(
		<div
			ref={dropdownRef}
			className={className}
			style={{ top: position.top, left: position.left }}
			onMouseDown={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		document.body,
	);
}
